import React, { memo, useState, useEffect } from 'react';
import { TreeView, TreeItem, TreeViewProps } from '@mui/lab';
import FormControlLabel from '@mui/material/FormControlLabel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { styled } from '@mui/material';

import Box from '../Box/Box';
import { ROOT_ID, CheckboxState, Item, Id, ItemsMap, ParentsMap, Checker, ToggleHandler } from './useTreeData';
import Typography from '../Typography/Typography';
import { Checkbox } from 'ui';

export type TreeLayoutProps = {
  items: ItemsMap;
  parentsMap: ParentsMap;
  onSelectItems: (changedItems: Item[]) => void;
  filterQuery: string;
  expanded: Id[];
} & TreeViewProps &
  SharedProps;

type BranchProps = {
  branchId: Id;
} & SharedProps;

type SharedProps = {
  getChildren: (id: Id) => Item[];
  isParent: Checker;
  getData: (id: Id) => Item;
  toggleItem: ToggleHandler;
  hasSomeDescendantSelected: Checker;
  hasAllChildrenSelected: Checker;
  toggleAllChildren: ToggleHandler;
  toggleBranch?: ToggleHandler;
  layout?: 'simple' | 'default';
  isTreeFiltered?: () => boolean;
};

const StyledTreeItem = styled(TreeItem)<{ invisible: 'invisible' | undefined }>(({ invisible }) => ({
  '& > div.MuiTreeItem-content': {
    display: invisible ? 'none' : 'flex'
  }
}));

// Component 'Branch' with 'all' control element to toggle children
const Branch = memo(
  ({
    branchId,
    getChildren,
    isParent,
    getData,
    toggleItem,
    hasSomeDescendantSelected,
    hasAllChildrenSelected,
    toggleAllChildren,
    isTreeFiltered = () => false
  }: BranchProps & { isTreeFiltered: () => boolean }) => {
    const children = getChildren(branchId);
    const branchData = getData(branchId);

    return (
      <StyledTreeItem
        key={branchId}
        nodeId={String(branchId)}
        // prevent react warning when setting HTML attr
        invisible={branchData.invisible ? 'invisible' : undefined}
        label={
          <FormControlLabel
            control={
              <Checkbox
                checked={branchData.state === CheckboxState.CHECKED}
                indeterminate={hasSomeDescendantSelected(branchId) && branchData.state === CheckboxState.UNCHECKED}
                onChange={event => toggleItem(branchId, event.target.checked)}
                onClick={e => e.stopPropagation()}
                disabled={branchData.disabled}
              />
            }
            label={
              <Typography variant="subtitle2" color={branchData.disabled ? 'text.secondary' : 'text.primary'}>
                {branchData.name}
              </Typography>
            }
          />
        }
      >
        {isParent(branchId) && (
          <>
            {/* hide 'All' control el when is in filtered state */}
            {!isTreeFiltered() && (
              <FormControlLabel
                sx={{ marginLeft: '20px' }}
                control={
                  <Checkbox
                    checked={hasAllChildrenSelected(branchId)}
                    onChange={event => toggleAllChildren(branchId, event.target.checked)}
                    onClick={e => e.stopPropagation()}
                    disabled={branchData.disabled}
                  />
                }
                label={
                  <Typography variant="subtitle2" color={branchData.disabled ? 'text.secondary' : 'text.primary'}>
                    All
                  </Typography>
                }
              />
            )}
            {children.map((itemData: Item) => {
              return (
                <Branch
                  key={itemData.id}
                  branchId={itemData.id}
                  getChildren={getChildren}
                  isParent={isParent}
                  getData={getData}
                  toggleItem={toggleItem}
                  hasSomeDescendantSelected={hasSomeDescendantSelected}
                  hasAllChildrenSelected={hasAllChildrenSelected}
                  toggleAllChildren={toggleAllChildren}
                  isTreeFiltered={isTreeFiltered}
                />
              );
            })}
          </>
        )}
      </StyledTreeItem>
    );
  }
);
const SimpleBranch = memo(
  ({
    branchId,
    getChildren,
    isParent,
    getData,
    toggleItem,
    hasSomeDescendantSelected,
    hasAllChildrenSelected,
    toggleAllChildren,
    toggleBranch = () => {}
  }: BranchProps) => {
    const children = getChildren(branchId);
    const branchData = getData(branchId);

    return (
      <StyledTreeItem
        key={branchId}
        nodeId={String(branchId)}
        invisible={branchData.invisible ? 'invisible' : undefined}
        label={
          <FormControlLabel
            control={
              <Checkbox
                checked={branchData.state === CheckboxState.CHECKED}
                indeterminate={
                  isParent(branchId) &&
                  hasSomeDescendantSelected(branchId) &&
                  branchData.state === CheckboxState.UNCHECKED
                }
                onChange={event => {
                  if (isParent(branchId)) {
                    toggleBranch(branchId, event.target.checked);
                  } else {
                    toggleItem(branchId, event.target.checked);
                  }
                }}
                onClick={e => e.stopPropagation()}
                disabled={branchData.disabled}
              />
            }
            label={
              <Typography variant="subtitle2" color={branchData.disabled ? 'text.secondary' : 'text.primary'}>
                {branchData.name}
              </Typography>
            }
          />
        }
      >
        {isParent(branchId) &&
          children.map((itemData: Item) => {
            return (
              <SimpleBranch
                key={itemData.id}
                branchId={itemData.id}
                getChildren={getChildren}
                isParent={isParent}
                getData={getData}
                toggleItem={toggleItem}
                hasSomeDescendantSelected={hasSomeDescendantSelected}
                hasAllChildrenSelected={hasAllChildrenSelected}
                toggleAllChildren={toggleAllChildren}
                toggleBranch={toggleBranch}
              />
            );
          })}
      </StyledTreeItem>
    );
  }
);

/**
 * Tree Layout component to represent flat items map in a recursive tree
 * It takes items map and a bunch of imperative methods (toggleItem, toggleBranch, etc.)
 * to handle the tree data.
 * It has two layout types:
 * - Default - with virtual element under each branch to toggle all children items ('All' button)
 * - Simple - without virtual element. Branch toggling is being achieved by branch root control element.
 */
const TreeLayout = memo(
  ({
    items,
    parentsMap,
    layout,
    onSelectItems,
    filterQuery,
    getChildren,
    isParent,
    getData,
    toggleItem,
    hasSomeDescendantSelected,
    hasAllChildrenSelected,
    toggleBranch,
    toggleAllChildren,
    isTreeFiltered = () => false,
    expanded,
    ...otherProps
  }: TreeLayoutProps) => {
    const BranchElement = layout === 'default' ? Branch : SimpleBranch;
    const [expandedList, setExpandedList] = useState<string[]>([]);
    const handleExpandClick = (_: React.SyntheticEvent, nodeIds: string[]) => {
      setExpandedList(nodeIds);
    };
    useEffect(() => {
      setExpandedList(expanded);
    }, [expanded]);

    return (
      <Box sx={{ maxHeight: '40vh', overflow: 'auto' }}>
        {Object.keys(items).length > 0 ? (
          <TreeView
            defaultCollapseIcon={<ExpandMoreIcon />}
            defaultExpandIcon={<ChevronRightIcon />}
            sx={{ color: 'text.primary' }}
            onNodeToggle={handleExpandClick}
            expanded={expandedList}
            {...otherProps}
          >
            {parentsMap[ROOT_ID]?.map((id: Id) => (
              <BranchElement
                key={id}
                branchId={id}
                getChildren={getChildren}
                isParent={isParent}
                getData={getData}
                toggleItem={toggleItem}
                hasSomeDescendantSelected={hasSomeDescendantSelected}
                hasAllChildrenSelected={hasAllChildrenSelected}
                toggleAllChildren={toggleAllChildren}
                toggleBranch={toggleBranch}
                isTreeFiltered={isTreeFiltered}
              />
            ))}
          </TreeView>
        ) : (
          <Box
            sx={{
              height: '300px',
              width: '100%',
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'center'
            }}
          >
            <Typography>LOADING...</Typography>
          </Box>
        )}
      </Box>
    );
  }
);

export default TreeLayout;
